import { useEffect, useState } from "react";
import { ArrowUpRight, X } from "lucide-react";
import { API } from "@/lib/api";

// TODO: Replace TestFlight URL when iOS build opens
const PLAY_INTERNAL_TEST_URL = "https://play.google.com/apps/internaltest/4701465358857330345";
const TESTFLIGHT_URL = ""; // e.g. "https://testflight.apple.com/join/XXXXXX"

function isIOS() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function fireEvent(name, source, target) {
  try {
    const body = JSON.stringify({ name, source, target });
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(`${API}/analytics/event`, blob);
    } else {
      fetch(`${API}/analytics/event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch (_) {
    /* ignore */
  }
}

export default function AnnouncementBar() {
  const [dismissed, setDismissed] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    setIos(isIOS());
    try {
      if (sessionStorage.getItem("ix.ann.dismissed") === "1") setDismissed(true);
    } catch (_) {
      /* ignore */
    }
  }, []);

  const dismiss = () => {
    try {
      sessionStorage.setItem("ix.ann.dismissed", "1");
    } catch (_) {
      /* ignore */
    }
    setDismissed(true);
    fireEvent("announcement_dismiss", ios ? "ios" : "play", null);
  };

  if (dismissed) return null;

  // iOS gets the TestFlight variant (purple + yellow text); everyone else gets Play (yellow + black)
  const iosLive = ios && TESTFLIGHT_URL;
  const iosPending = ios && !TESTFLIGHT_URL;

  if (iosLive) return <TestFlightBar url={TESTFLIGHT_URL} onDismiss={dismiss} />;
  if (iosPending) return <TestFlightWaitlistBar onDismiss={dismiss} />;
  return <PlayBar url={PLAY_INTERNAL_TEST_URL} onDismiss={dismiss} />;
}

// ─── Google Play variant (yellow + black) ───────────────────────────────
function PlayBar({ url, onDismiss }) {
  return (
    <div
      className="fixed top-0 inset-x-0 z-[60] bg-lime text-black"
      data-testid="announcement-bar"
      data-variant="play"
      role="region"
      aria-label="Announcement"
    >
      <div className="container-x flex items-center justify-center gap-2 py-2 text-center">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => fireEvent("announcement_click", "play", url)}
          className="inline-flex items-center gap-2 text-[13px] md:text-sm font-semibold tracking-tight text-black hover:underline underline-offset-4 decoration-2"
          data-testid="announcement-cta"
        >
          <span aria-hidden className="hidden sm:inline text-[10px] tracking-[0.22em] uppercase font-bold px-2 py-0.5 rounded-full bg-black text-lime">
            Beta
          </span>
          <span>
            Join our Internal Beta on Google Play{" "}
            <span className="hidden sm:inline">— Click here to test!</span>
          </span>
          <ArrowUpRight className="w-4 h-4" strokeWidth={2.4} />
        </a>
        <DismissButton onClick={onDismiss} tone="dark" />
      </div>
    </div>
  );
}

// ─── TestFlight live variant (purple + yellow text) ─────────────────────
function TestFlightBar({ url, onDismiss }) {
  return (
    <div
      className="fixed top-0 inset-x-0 z-[60] bg-forest text-canvas"
      data-testid="announcement-bar"
      data-variant="testflight"
      role="region"
      aria-label="TestFlight"
    >
      <div className="container-x flex items-center justify-center gap-2 py-2 text-center">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => fireEvent("announcement_click", "testflight", url)}
          className="inline-flex items-center gap-2 text-[13px] md:text-sm font-semibold tracking-tight text-canvas hover:text-lime transition-colors"
          data-testid="announcement-cta"
        >
          <span aria-hidden className="hidden sm:inline text-[10px] tracking-[0.22em] uppercase font-bold px-2 py-0.5 rounded-full bg-lime text-forest">
            Beta · iOS
          </span>
          <span className="text-lime">
            Join our TestFlight for iPhone{" "}
            <span className="hidden sm:inline">— Click here to test!</span>
          </span>
          <ArrowUpRight className="w-4 h-4 text-lime" strokeWidth={2.4} />
        </a>
        <DismissButton onClick={onDismiss} tone="light" />
      </div>
    </div>
  );
}

// ─── TestFlight coming-soon variant (iOS visitors, no link yet) ─────────
function TestFlightWaitlistBar({ onDismiss }) {
  return (
    <div
      className="fixed top-0 inset-x-0 z-[60] bg-forest text-canvas"
      data-testid="announcement-bar"
      data-variant="testflight-pending"
      role="region"
      aria-label="TestFlight coming soon"
    >
      <div className="container-x flex items-center justify-center gap-2 py-2 text-center">
        <a
          href="/waitlist?ios=1"
          onClick={() => fireEvent("announcement_click", "testflight-pending", "/waitlist")}
          className="inline-flex items-center gap-2 text-[13px] md:text-sm font-semibold tracking-tight text-canvas hover:text-lime transition-colors"
          data-testid="announcement-cta"
        >
          <span aria-hidden className="hidden sm:inline text-[10px] tracking-[0.22em] uppercase font-bold px-2 py-0.5 rounded-full bg-lime text-forest">
            Beta · iOS
          </span>
          <span>
            Testing on iPhone?{" "}
            <span className="text-lime">Reserve your TestFlight seat</span>{" "}
            <span className="hidden sm:inline text-canvas/70">— shipping next</span>
          </span>
          <ArrowUpRight className="w-4 h-4 text-lime" strokeWidth={2.4} />
        </a>
        <DismissButton onClick={onDismiss} tone="light" />
      </div>
    </div>
  );
}

function DismissButton({ onClick, tone }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Dismiss announcement"
      data-testid="announcement-dismiss"
      className={`absolute right-3 md:right-5 top-1/2 -translate-y-1/2 p-1 rounded-full transition-colors ${
        tone === "light" ? "hover:bg-canvas/15" : "hover:bg-black/10"
      }`}
    >
      <X className="w-3.5 h-3.5" strokeWidth={2.4} />
    </button>
  );
}
